import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSurvey } from '../../context/SurveyContext';
import { validateField } from '../../utils/validation';

// Form inputs
import TextInput from '../FormInputs/TextInput';
import EmailInput from '../FormInputs/EmailInput';
import PhoneInput from '../FormInputs/PhoneInput';
import DateInput from '../FormInputs/DateInput';
import Textarea from '../FormInputs/Textarea';
import RadioGroup from '../FormInputs/RadioGroup';
import StateDropdown from '../FormInputs/StateDropdown';
import HeightDropdown from '../FormInputs/HeightDropdown';
import WeightInput from '../FormInputs/WeightInput';
import AddressAutocomplete from '../FormInputs/AddressAutocomplete';
import Checkbox from '../FormInputs/Checkbox';

export default function QuestionPage({ question, onValidate }) {
  // Multi-field page (like page 1 & 2) - Check BEFORE any hooks
  if (question.fields) {
    return <MultiFieldPage question={question} onValidate={onValidate} />;
  }

  // Single question page - Hooks are now called consistently
  const { answers, updateAnswer } = useSurvey();
  const [errors, setErrors] = useState({});

  // Single question page
  const value = answers[question.id] || '';
  const error = errors[question.id] || '';

  const handleChange = (newValue) => {
    updateAnswer(question.id, newValue);
    // Clear error when user starts typing
    if (error) {
      setErrors({});
    }
  };

  const handleValidate = () => {
    const validation = validateField(question, value);
    if (!validation.valid) {
      setErrors({ [question.id]: validation.message });
      onValidate(false);
      return false;
    }
    onValidate(true);
    return true;
  };

  // Expose validation to parent
  useEffect(() => {
    // For textarea fields, allow empty (will submit "None" to API)
    // For other fields, only validate if there's a value
    if (question.type === 'textarea') {
      // Textarea fields are always valid (empty is OK, will send "None" to API)
      onValidate(true);
    } else if (value) {
      handleValidate();
    } else {
      onValidate(false);
    }
  }, [value, question.type, question.id]); // Added question.id to re-validate on question change

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      {renderInput(question, value, handleChange, error)}
    </motion.div>
  );
}

function MultiFieldPage({ question, onValidate }) {
  const { answers, updateAnswer } = useSurvey();
  const [errors, setErrors] = useState({});

  const handleFieldChange = (fieldId, value) => {
    updateAnswer(fieldId, value);
    // Clear error for this field
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // Auto-fill city and ZIP when address is selected from Google autocomplete
  const handleAddressSelected = (placeData) => {
    if (placeData.city) {
      updateAnswer('shippingCity', placeData.city);
    }
    if (placeData.zipCode) {
      updateAnswer('shippingZip', placeData.zipCode);
    }
  };

  const validateAll = () => {
    const newErrors = {};
    let isValid = true;

    question.fields.forEach(field => {
      const value = answers[field.id] || '';
      const validation = validateField(field, value);
      if (!validation.valid) {
        newErrors[field.id] = validation.message;
        isValid = false;
      }
    });

    setErrors(newErrors);
    onValidate(isValid);
    return isValid;
  };

  useEffect(() => {
    // Validate when any field changes
    // Only check required fields (optional fields don't need to be filled)
    const hasAllRequiredValues = question.fields
      .filter(f => f.required !== false)
      .every(f => answers[f.id]);

    if (hasAllRequiredValues) {
      validateAll();
    } else {
      onValidate(false);
    }
  }, [answers]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <h2 className="text-2xl md:text-3xl font-bold text-charcoal mb-6">
        {question.page === 17 && "Tell us about yourself"}
        {question.page === 18 && "How can we reach you?"}
        {question.page === 20 && "Almost done! Delivery details"}
      </h2>

      <div className="space-y-6">
        {question.fields.map(field => (
          <div key={field.id}>
            {renderInput(
              field,
              answers[field.id] || '',
              (value) => handleFieldChange(field.id, value),
              errors[field.id] || '',
              field.type === 'address' ? handleAddressSelected : undefined
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function renderInput(field, value, onChange, error, onPlaceSelected) {
  const commonProps = {
    id: field.id,
    label: field.label || field.text,
    value,
    onChange,
    placeholder: field.placeholder,
    required: field.required,
    error
  };

  switch (field.type) {
    case 'text':
      return <TextInput {...commonProps} />;

    case 'email':
      return <EmailInput {...commonProps} />;

    case 'phone':
      return <PhoneInput {...commonProps} />;

    case 'date':
      return <DateInput {...commonProps} />;

    case 'textarea':
      return <Textarea {...commonProps} rows={field.rows || 4} />;

    case 'radio':
      return <RadioGroup {...commonProps} options={field.options} />;

    case 'state':
      return <StateDropdown {...commonProps} />;

    case 'height':
      return <HeightDropdown {...commonProps} />;

    case 'number':
      return <WeightInput {...commonProps} />;

    case 'address':
      return <AddressAutocomplete {...commonProps} onPlaceSelected={onPlaceSelected} />;

    case 'checkbox':
      return <Checkbox {...commonProps} />;

    default:
      return <TextInput {...commonProps} />;
  }
}
